import { useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Check, EyeOff, Flag, Library, ShieldAlert, X } from "lucide-react";
import {
  fetchLibrary,
  fetchReports,
  resolveReport,
  setLibraryStatus,
} from "@/lib/queries";
import { date, number, titleCase } from "@/lib/format";
import type { LibraryDeck, LibraryReport } from "@/lib/types";
import { Modal } from "@/components/Modal";

type Tab = LibraryDeck["status"] | "all";

const tabs: Array<{ key: Tab; label: string }> = [
  { key: "pending", label: "Pending" },
  { key: "approved", label: "Approved" },
  { key: "rejected", label: "Rejected" },
  { key: "all", label: "All" },
];

function statusBadge(status: LibraryDeck["status"]) {
  const map = {
    pending: "badge-sunshine",
    approved: "badge-mint",
    rejected: "badge-danger",
    hidden: "badge-rose",
  } as const;
  return `badge ${map[status]}`;
}

export function LibraryPage() {
  const queryClient = useQueryClient();
  const [tab, setTab] = useState<Tab>("pending");
  const [rejecting, setRejecting] = useState<LibraryDeck | null>(null);
  const [note, setNote] = useState("");
  const [notice, setNotice] = useState<string | null>(null);

  const decks = useQuery({
    queryKey: ["library", tab],
    queryFn: () => fetchLibrary(tab),
  });
  const reports = useQuery({ queryKey: ["reports"], queryFn: fetchReports });

  const statusMutation = useMutation({
    mutationFn: (input: { id: string; status: LibraryDeck["status"]; note?: string }) =>
      setLibraryStatus(input.id, input.status, input.note),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["library"] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
      setNotice("Library updated.");
      setRejecting(null);
      setNote("");
    },
    onError: (error: Error) => setNotice(error.message),
  });

  const reportMutation = useMutation({
    mutationFn: (input: { id: string; status: LibraryReport["status"] }) =>
      resolveReport(input.id, input.status),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["reports"] });
      void queryClient.invalidateQueries({ queryKey: ["overview"] });
    },
  });

  const pendingReports = (reports.data ?? []).filter((report) => report.status === "open");

  return (
    <>
      <div className="page-head">
        <div>
          <h2 style={{ fontSize: 15 }}>Public deck library</h2>
          <p>Every deck is screened by AI before it reaches this queue. A human decides.</p>
        </div>
        <div className="segmented">
          {tabs.map((item) => (
            <button
              key={item.key}
              className={tab === item.key ? "active" : ""}
              onClick={() => setTab(item.key)}
            >
              {item.label}
            </button>
          ))}
        </div>
      </div>

      {notice && <div className="banner banner-info">{notice}</div>}

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Submissions</h2>
            <p>{number(decks.data?.length ?? 0)} decks in this view</p>
          </div>
          <div className="spacer" />
          <span className="badge badge-sunshine">
            <Library size={12} />
            {number(pendingReports.length)} open reports
          </span>
        </div>

        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Deck</th>
                <th>Owner</th>
                <th>Subject</th>
                <th>Cards</th>
                <th>AI score</th>
                <th>Installs</th>
                <th>Status</th>
                <th>Created</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {decks.isLoading && (
                <tr><td colSpan={9} className="empty"><div className="spinner" /></td></tr>
              )}
              {!decks.isLoading && (decks.data ?? []).length === 0 && (
                <tr><td colSpan={9} className="empty">Nothing in this queue.</td></tr>
              )}
              {(decks.data ?? []).map((deck) => (
                <tr key={deck.id}>
                  <td>
                    <div className="cell-strong">{deck.title}</div>
                    <div className="cell-sub" style={{ maxWidth: 320, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {deck.description ?? "No description"}
                    </div>
                  </td>
                  <td className="muted">{deck.profiles?.display_name ?? "deleted user"}</td>
                  <td className="muted">{deck.subject ?? "—"}</td>
                  <td>{number(deck.card_count)}</td>
                  <td>
                    {deck.ai_screen_score === null ? (
                      <div>
                        <span className="faint">—</span>
                        {deck.library_screen_results &&
                          deck.library_screen_results.attempts >= 3 && (
                            <div className="cell-sub" style={{ maxWidth: 160 }}>
                              AI unavailable
                            </div>
                          )}
                      </div>
                    ) : (
                      <div>
                        <span className={`badge ${deck.ai_screen_score < 0.3 ? "badge-danger" : deck.ai_screen_score < 0.7 ? "badge-sunshine" : "badge-mint"}`}>
                          {deck.ai_screen_score.toFixed(2)}
                        </span>
                        {deck.library_screen_results?.note && (
                          <div
                            className="cell-sub"
                            title={deck.library_screen_results.note}
                            style={{ maxWidth: 180, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}
                          >
                            {deck.library_screen_results.note}
                          </div>
                        )}
                      </div>
                    )}
                  </td>
                  <td>{number(deck.installs)}</td>
                  <td><span className={statusBadge(deck.status)}>{deck.status}</span></td>
                  <td className="nowrap faint">{date(deck.created_at)}</td>
                  <td>
                    <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                      {deck.status !== "approved" && (
                        <button
                          className="btn btn-sm"
                          style={{ background: "var(--mint-wash)", color: "var(--mint-ink)" }}
                          disabled={statusMutation.isPending}
                          onClick={() => statusMutation.mutate({ id: deck.id, status: "approved" })}
                          title="Approve and publish"
                        >
                          <Check size={12} /> Approve
                        </button>
                      )}
                      {deck.status !== "rejected" && (
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => {
                            setRejecting(deck);
                            setNote("");
                          }}
                        >
                          <X size={12} /> Reject
                        </button>
                      )}
                      {deck.status === "approved" && (
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => statusMutation.mutate({ id: deck.id, status: "hidden" })}
                          title="Hide from the library"
                        >
                          <EyeOff size={12} />
                        </button>
                      )}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      <section className="card">
        <div className="card-head">
          <div>
            <h2>Reports</h2>
            <p>User reports on published decks</p>
          </div>
          <div className="spacer" />
          <span className="badge badge-rose">
            <Flag size={12} />
            {number(pendingReports.length)} open
          </span>
        </div>
        <div className="table-wrap" style={{ marginTop: 14 }}>
          <table className="table">
            <thead>
              <tr>
                <th>Deck</th>
                <th>Reporter</th>
                <th>Reason</th>
                <th>Details</th>
                <th>When</th>
                <th>Status</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {reports.isLoading && (
                <tr><td colSpan={7} className="empty"><div className="spinner" /></td></tr>
              )}
              {!reports.isLoading && (reports.data ?? []).length === 0 && (
                <tr><td colSpan={7} className="empty">No reports. Nice.</td></tr>
              )}
              {(reports.data ?? []).map((report) => (
                <tr key={report.id}>
                  <td className="cell-strong">{report.library_decks?.title ?? "—"}</td>
                  <td className="muted">{report.profiles?.display_name ?? "—"}</td>
                  <td><span className="badge badge-rose">{titleCase(report.reason)}</span></td>
                  <td className="muted" style={{ maxWidth: 360, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                    {report.details ?? "—"}
                  </td>
                  <td className="nowrap faint">{date(report.created_at)}</td>
                  <td>
                    <span className={`badge ${report.status === "open" ? "badge-sunshine" : report.status === "actioned" ? "badge-mint" : ""}`}>
                      {report.status}
                    </span>
                  </td>
                  <td>
                    {report.status === "open" && (
                      <div className="row" style={{ gap: 4, justifyContent: "flex-end" }}>
                        <button
                          className="btn btn-sm btn-ghost"
                          onClick={() => reportMutation.mutate({ id: report.id, status: "dismissed" })}
                        >
                          Dismiss
                        </button>
                        <button
                          className="btn btn-sm btn-danger"
                          onClick={() => reportMutation.mutate({ id: report.id, status: "actioned" })}
                        >
                          <ShieldAlert size={12} /> Action
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {rejecting && (
        <Modal
          title="Reject this deck?"
          subtitle={rejecting.title}
          onClose={() => setRejecting(null)}
        >
          <div className="field">
            <label htmlFor="note">Moderation note (visible to the owner)</label>
            <textarea
              id="note"
              className="textarea"
              placeholder="Explain what needs to change…"
              value={note}
              onChange={(event) => setNote(event.target.value)}
            />
          </div>
          <div className="modal-actions">
            <button className="btn btn-ghost" onClick={() => setRejecting(null)}>
              Cancel
            </button>
            <button
              className="btn btn-danger"
              disabled={statusMutation.isPending}
              onClick={() =>
                statusMutation.mutate({
                  id: rejecting.id,
                  status: "rejected",
                  note: note.trim() || undefined,
                })
              }
            >
              Reject deck
            </button>
          </div>
        </Modal>
      )}
    </>
  );
}
